import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputAdornment,
  Alert,
  Chip,
  Stack,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { formatCurrency } from '../utils/formatters';
import { Recommendation, Portfolio } from '../types';
import { PositionService } from '../services/positionService';
import { PortfolioService } from '../services/portfolioService';
import { RecommendationService } from '../services/recommendationService';
import {
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Settings,
  Warning,
  Info,
} from '@mui/icons-material';

interface RecommendationExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: Recommendation | null;
  portfolio: Portfolio | null;
  availableFunds: number;
  onTradeExecuted: () => void;
}

type OrderType = 'market' | 'limit';
type TradeStatus = 'pending' | 'executed';

export default function RecommendationExecutionDialog({
  open,
  onClose,
  recommendation,
  portfolio,
  availableFunds,
  onTradeExecuted,
}: RecommendationExecutionDialogProps) {
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [tradeStatus, setTradeStatus] = useState<TradeStatus>('pending');
  const [quantity, setQuantity] = useState<number>(0);
  const [executionPrice, setExecutionPrice] = useState<string>('');
  const [stopLoss, setStopLoss] = useState<string>('');
  const [targetPrice, setTargetPrice] = useState<string>('');
  const [executionDate, setExecutionDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd'T'HH:mm")
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [alpacaStatus, setAlpacaStatus] = useState<string | null>(null);

  // Pre-fill values from recommendation
  useEffect(() => {
    if (recommendation && recommendation.type === 'entry') {
      // Set recommended values
      setQuantity(recommendation.recommendedQuantity || 0);
      setExecutionPrice((recommendation.recommendedEntryPrice || recommendation.currentPrice).toFixed(2));
      setStopLoss((recommendation.recommendedStopLoss || 0).toFixed(2));
      setTargetPrice((recommendation.recommendedTarget || 0).toFixed(2));
      
      // Set order type based on recommendation
      if (recommendation.orderType === 'limit' || recommendation.orderType === 'stop') {
        setOrderType('limit');
      } else {
        setOrderType('market');
      }
    } else if (recommendation && recommendation.type === 'adjustment') {
      // For adjustments, pre-fill the adjustment value
      if (recommendation.adjustmentValue) {
        if (recommendation.adjustmentType === 'raise_stop') {
          setStopLoss(recommendation.adjustmentValue.toFixed(2));
        } else if (recommendation.adjustmentType === 'lower_target' || recommendation.adjustmentType === 'raise_target') {
          // Both lower_target and raise_target modify the target price
          setTargetPrice(recommendation.adjustmentValue.toFixed(2));
        }
      }
    }
  }, [recommendation]);

  const handleExecuteTrade = async () => {
    if (!recommendation || !portfolio) return;

    setError(null);
    setLoading(true);

    try {
      if (recommendation.type === 'entry' && recommendation.action === 'buy') {
        const price = parseFloat(executionPrice);
        const totalCost = price * quantity;

        if (totalCost > availableFunds) {
          throw new Error('Insufficient funds for this trade');
        }

        // Route through Alpaca if portfolio is linked
        if (portfolio.alpacaLinked) {
          setAlpacaStatus('Submitting to Alpaca...');
          const executeAlpacaTrade = httpsCallable<{
            side: 'buy' | 'sell';
            symbol: string;
            qty: number;
            portfolioId: string;
            recommendationId: string;
            stopLoss?: number;
            targetPrice?: number;
          }, {
            success: boolean;
            filledPrice: number;
            filledQty: number;
            orderId: string;
            positionId: string;
          }>(functions, 'executeAlpacaTrade');

          setAlpacaStatus('Waiting for Alpaca fill...');
          const result = await executeAlpacaTrade({
            side: 'buy',
            symbol: recommendation.symbol,
            qty: quantity,
            portfolioId: portfolio.id,
            recommendationId: recommendation.id,
            stopLoss: parseFloat(stopLoss) || undefined,
            targetPrice: parseFloat(targetPrice) || undefined,
          });

          setAlpacaStatus(`Filled: ${result.data.filledQty} shares @ $${result.data.filledPrice.toFixed(2)}`);
          // Cloud function handles all Firestore writes, so we skip to onTradeExecuted
        } else {
          // Non-Alpaca: write directly to Firestore from frontend
          // Determine position status based on order type and trade status
          const positionStatus = orderType === 'limit' && tradeStatus === 'pending' ? 'pending' : 'open';

          // Parse execution date if provided (for executed trades)
          const executionDateObj = (orderType === 'market' || tradeStatus === 'executed') && executionDate
            ? new Date(executionDate)
            : undefined;

          // Open the position (or create pending position)
          await PositionService.openPosition(
            portfolio.id,
            recommendation.symbol,
            quantity,
            price,
            parseFloat(stopLoss),
            parseFloat(targetPrice),
            orderType,
            positionStatus,
            executionDateObj
          );

          // Update portfolio available funds
          const updatedPortfolio = await PortfolioService.getPortfolio(portfolio.id);
          if (updatedPortfolio) {
            const openPositions = await PositionService.getOpenPositions(portfolio.id);
            const openPositionsCost = openPositions.reduce(
              (sum, p) => sum + (p.entryPrice * p.quantity),
              0
            );

            const realizedLosses = Math.min(0, updatedPortfolio.totalEarnings || 0);
            const newAvailableFunds = updatedPortfolio.initialFundValue - openPositionsCost + realizedLosses;

            await PortfolioService.updateAvailableFunds(portfolio.id, newAvailableFunds);
          }

          // Mark recommendation as executed (Alpaca path does this in the Cloud Function)
          await RecommendationService.markRecommendationExecuted(recommendation.id);
        }
      } else if (recommendation.type === 'exit' && recommendation.action === 'sell') {
        // Close position
        if (recommendation.positionId) {
          const position = await PositionService.getPosition(recommendation.positionId);
          if (position) {
            await PositionService.closePosition(
              position.id,
              parseFloat(executionPrice) || recommendation.currentPrice,
              new Date(executionDate)
            );
          }
        }
      } else if (recommendation.type === 'adjustment') {
        // Apply adjustments
        if (recommendation.positionId) {
          if (recommendation.adjustmentType === 'raise_stop') {
            await PositionService.updateStopLoss(
              recommendation.positionId,
              parseFloat(stopLoss)
            );
          } else if (recommendation.adjustmentType === 'lower_target' || recommendation.adjustmentType === 'raise_target') {
            // Both lower_target and raise_target update the target price
            await PositionService.updateTargetPrice(
              recommendation.positionId,
              parseFloat(targetPrice)
            );
          }
        }
      }

      // Mark recommendation as executed
      // (Alpaca buy path handles this in the Cloud Function; non-Alpaca buy handles it above)
      const isAlpacaBuy = recommendation.type === 'entry' && recommendation.action === 'buy' && portfolio.alpacaLinked;
      if (!isAlpacaBuy) {
        await RecommendationService.markRecommendationExecuted(recommendation.id);
      }

      onTradeExecuted();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to execute trade');
    } finally {
      setLoading(false);
      setAlpacaStatus(null);
    }
  };

  const handleClose = () => {
    setOrderType('market');
    setTradeStatus('pending');
    setQuantity(0);
    setExecutionPrice('');
    setStopLoss('');
    setTargetPrice('');
    setExecutionDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError(null);
    setAlpacaStatus(null);
    onClose();
  };

  if (!recommendation || !portfolio) return null;

  const totalCost = parseFloat(executionPrice || '0') * quantity;
  const percentOfFunds = availableFunds > 0 ? (totalCost / availableFunds) * 100 : 0;

  const getRecommendationIcon = () => {
    if (recommendation.type === 'entry') return <TrendingUp color="success" />;
    if (recommendation.type === 'exit') return <TrendingDown color="error" />;
    if (recommendation.type === 'adjustment') return <Settings color="info" />;
    return <Info />;
  };

  const getDialogTitle = () => {
    if (recommendation.type === 'entry') return `Execute Buy Order - ${recommendation.symbol}`;
    if (recommendation.type === 'exit') return `Execute Sell Order - ${recommendation.symbol}`;
    if (recommendation.type === 'adjustment') return `Apply Adjustment - ${recommendation.symbol}`;
    return `Execute Recommendation - ${recommendation.symbol}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {getRecommendationIcon()}
          <Box>
            <Typography variant="h6">{getDialogTitle()}</Typography>
            <Typography variant="body2" color="textSecondary">
              Portfolio: {portfolio.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Recommendation Context */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Recommendation Details
            </Typography>
            <Chip 
              label={recommendation.urgency} 
              color={
                recommendation.urgency === 'immediate' ? 'error' :
                recommendation.urgency === 'high' ? 'warning' :
                recommendation.urgency === 'medium' ? 'info' : 'default'
              } 
              size="small" 
            />
            <Chip
              label={`Score: ${recommendation.score}`}
              size="small"
              variant="outlined"
            />
          </Box>
          
          <Stack spacing={0.5}>
            {recommendation.reasons.map((reason, i) => (
              <Typography key={i} variant="body2" color="textSecondary">
                • {reason}
              </Typography>
            ))}
          </Stack>

          {/* Technical Factors */}
          {recommendation.technicalFactors && Object.keys(recommendation.technicalFactors).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="textSecondary">
                Technical Factors: {Object.entries(recommendation.technicalFactors)
                  .filter(([_, value]) => value !== undefined)
                  .map(([key, value]) => `${key}: ${typeof value === 'number' ? value.toFixed(2) : value}`)
                  .join(' | ')}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Current Market Info */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2}>
            <Chip 
              label={`Current Price: ${formatCurrency(recommendation.currentPrice)}`} 
              color="primary" 
            />
            <Chip 
              label={`Available Funds: ${formatCurrency(availableFunds)}`} 
              color="info" 
            />
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {alpacaStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {alpacaStatus}
          </Alert>
        )}

        {/* Entry Trade Form */}
        {recommendation.type === 'entry' && (
          <>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Order Type</FormLabel>
              <RadioGroup
                row
                value={orderType}
                onChange={(e) => setOrderType(e.target.value as OrderType)}
              >
                <FormControlLabel 
                  value="market" 
                  control={<Radio />} 
                  label="Market Order (Execute Now)" 
                />
                <FormControlLabel 
                  value="limit" 
                  control={<Radio />} 
                  label="Limit Order (Pending)" 
                />
              </RadioGroup>
            </FormControl>

            {orderType === 'limit' && (
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">Trade Status</FormLabel>
                <RadioGroup
                  row
                  value={tradeStatus}
                  onChange={(e) => setTradeStatus(e.target.value as TradeStatus)}
                >
                  <FormControlLabel 
                    value="pending" 
                    control={<Radio />} 
                    label="Still Pending" 
                  />
                  <FormControlLabel 
                    value="executed" 
                    control={<Radio />} 
                    label="Executed" 
                  />
                </RadioGroup>
              </FormControl>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                InputProps={{
                  inputProps: { min: 1 }
                }}
                helperText={`Recommended: ${recommendation.recommendedQuantity || 'N/A'}`}
              />
              <TextField
                label={orderType === 'market' ? 'Execution Price' : 'Limit Price'}
                type="number"
                fullWidth
                value={executionPrice}
                onChange={(e) => setExecutionPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01 }
                }}
                helperText={`Recommended: ${formatCurrency(recommendation.recommendedEntryPrice || recommendation.currentPrice)}`}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <TextField
                label="Stop Loss"
                type="number"
                fullWidth
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01 }
                }}
                helperText={`Recommended: ${formatCurrency(recommendation.recommendedStopLoss || 0)}`}
              />
              <TextField
                label="Target Price"
                type="number"
                fullWidth
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01 }
                }}
                helperText={`Recommended: ${formatCurrency(recommendation.recommendedTarget || 0)}`}
              />
            </Box>
          </>
        )}

        {/* Exit Trade Form */}
        {recommendation.type === 'exit' && (
          <>
            <Alert severity={recommendation.exitReason === 'stop_hit' ? 'error' : 'info'} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {recommendation.exitReason === 'stop_hit' && 'Stop Loss Hit - Exit position immediately to limit losses'}
                {recommendation.exitReason === 'target_hit' && 'Target Reached - Take profits at target price'}
                {recommendation.exitReason === 'time_limit' && 'Holding Time Exceeded - Exit to free up capital'}
                {recommendation.exitReason === 'stagnant' && 'Position Stagnant - Exit to reallocate funds'}
              </Typography>
            </Alert>

            <TextField
              label="Exit Price"
              type="number"
              fullWidth
              value={executionPrice || recommendation.currentPrice}
              onChange={(e) => setExecutionPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { step: 0.01 }
              }}
              sx={{ mb: 3 }}
            />
          </>
        )}

        {/* Adjustment Form */}
        {recommendation.type === 'adjustment' && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {recommendation.adjustmentType === 'raise_stop' && 'Raise stop loss to protect profits'}
                {recommendation.adjustmentType === 'lower_target' && 'Lower target to increase probability of profit-taking'}
                {recommendation.adjustmentType === 'raise_target' && 'Set or raise target price for profit-taking strategy'}
              </Typography>
            </Alert>

            {recommendation.adjustmentType === 'raise_stop' && (
              <TextField
                label="New Stop Loss"
                type="number"
                fullWidth
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01 }
                }}
                helperText={`Recommended: ${formatCurrency(recommendation.adjustmentValue || 0)}`}
                sx={{ mb: 3 }}
              />
            )}

            {(recommendation.adjustmentType === 'lower_target' || recommendation.adjustmentType === 'raise_target') && (
              <TextField
                label="New Target Price"
                type="number"
                fullWidth
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01 }
                }}
                helperText={`Recommended: ${formatCurrency(recommendation.adjustmentValue || 0)}`}
                sx={{ mb: 3 }}
              />
            )}
          </>
        )}

        {/* Execution Date/Time */}
        {((recommendation.type === 'entry' && (orderType === 'market' || tradeStatus === 'executed')) ||
          recommendation.type === 'exit') && (
          <TextField
            label="Execution Date/Time"
            type="datetime-local"
            fullWidth
            value={executionDate}
            onChange={(e) => setExecutionDate(e.target.value)}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}

        {/* Trade Summary for Entry */}
        {recommendation.type === 'entry' && (
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Trade Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Total Cost:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(totalCost)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                % of Available Funds:
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                color={percentOfFunds > 25 ? 'error' : 'success'}
              >
                {percentOfFunds.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Remaining Funds:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(availableFunds - totalCost)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Risk/Reward:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {(() => {
                  const entryPrice = parseFloat(executionPrice) || recommendation.currentPrice;
                  const stop = parseFloat(stopLoss) || 0;
                  const target = parseFloat(targetPrice) || 0;
                  const risk = entryPrice - stop;
                  const reward = target - entryPrice;
                  const ratio = risk > 0 ? reward / risk : 0;
                  return ratio > 0 ? `1:${ratio.toFixed(2)}` : 'N/A';
                })()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleExecuteTrade}
          disabled={
            loading || 
            (recommendation.type === 'entry' && (
              quantity <= 0 || 
              !executionPrice || 
              !stopLoss ||
              !targetPrice ||
              totalCost > availableFunds
            )) ||
            (recommendation.type === 'exit' && !executionPrice) ||
            (recommendation.type === 'adjustment' && (
              (recommendation.adjustmentType === 'raise_stop' && !stopLoss) ||
              ((recommendation.adjustmentType === 'lower_target' || recommendation.adjustmentType === 'raise_target') && !targetPrice)
            ))
          }
          color={recommendation.type === 'exit' ? 'error' : 'success'}
          startIcon={<CheckCircle />}
        >
          {recommendation.type === 'entry' && (
            portfolio?.alpacaLinked
              ? 'Submit Alpaca Buy'
              : orderType === 'market' ? 'Execute Buy' :
                tradeStatus === 'pending' ? 'Save Pending Order' : 'Record Executed Trade'
          )}
          {recommendation.type === 'exit' && 'Execute Sell'}
          {recommendation.type === 'adjustment' && 'Apply Adjustment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}